const UI_LOCALE = 'en-US';
const INVALID_VALUE = '—';
const MINUTE_IN_MS = 60_000;
const MINUTES_PER_HOUR = 60;
const HOURS_PER_DAY = 24;
const DAYS_PER_WEEK = 7;
const LONG_WAIT_DAYS = 90;

type UiDateInput = Date | number | string | null | undefined;

const parseUiDate = (
  value: UiDateInput,
  calendarDate = false
): Date | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  if (
    calendarDate &&
    typeof value === 'string' &&
    /^\d{4}-\d{2}-\d{2}$/.test(value)
  ) {
    const [year = Number.NaN, month = Number.NaN, day = Number.NaN] = value
      .split('-')
      .map(Number);
    const localDate = new Date(0);
    localDate.setHours(0, 0, 0, 0);
    localDate.setFullYear(year, month - 1, day);
    if (
      Number.isNaN(localDate.getTime())
      || localDate.getFullYear() !== year
      || localDate.getMonth() !== month - 1
      || localDate.getDate() !== day
    ) {
      return null;
    }
    return localDate;
  }

  const date = value instanceof Date ? new Date(value.getTime()) : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const formatUiDate = (
  value: UiDateInput,
  options: Intl.DateTimeFormatOptions = {}
): string => {
  const date = parseUiDate(value, true);
  if (!date) {
    return INVALID_VALUE;
  }

  return new Intl.DateTimeFormat(UI_LOCALE, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    ...options,
  }).format(date);
};

export const formatUiDateTime = (
  value: UiDateInput,
  options: Intl.DateTimeFormatOptions = {}
): string => {
  const date = parseUiDate(value);
  if (!date) {
    return INVALID_VALUE;
  }

  return new Intl.DateTimeFormat(UI_LOCALE, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    ...options,
  }).format(date);
};

/**
 * Formats elapsed approval time as a compact, human-readable phrase that can
 * follow "Waiting". Long waits become a calendar date because week counts stop
 * being useful once the operator is looking several months back.
 */
export const formatUiWaitingDuration = (
  value: UiDateInput,
  now: UiDateInput = new Date()
): string => {
  const startedAt = parseUiDate(value);
  const reference = parseUiDate(now);
  if (!startedAt || !reference) {
    return 'unknown time';
  }

  const elapsedMinutes = Math.floor(
    Math.max(0, reference.getTime() - startedAt.getTime()) / MINUTE_IN_MS
  );
  if (elapsedMinutes < 1) {
    return 'less than a minute';
  }
  if (elapsedMinutes < MINUTES_PER_HOUR) {
    return `${elapsedMinutes} min`;
  }

  const elapsedHours = Math.floor(elapsedMinutes / MINUTES_PER_HOUR);
  if (elapsedHours < HOURS_PER_DAY) {
    return `${elapsedHours} ${elapsedHours === 1 ? 'hour' : 'hours'}`;
  }

  const elapsedDays = Math.floor(elapsedHours / HOURS_PER_DAY);
  if (elapsedDays < DAYS_PER_WEEK) {
    return `${elapsedDays} ${elapsedDays === 1 ? 'day' : 'days'}`;
  }
  if (elapsedDays <= LONG_WAIT_DAYS) {
    const elapsedWeeks = Math.floor(elapsedDays / DAYS_PER_WEEK);
    return `${elapsedWeeks} ${elapsedWeeks === 1 ? 'week' : 'weeks'}`;
  }

  const dateParts = new Intl.DateTimeFormat(UI_LOCALE, {
    day: 'numeric',
    month: 'short',
  }).formatToParts(startedAt);
  const day = dateParts.find((part) => part.type === 'day')?.value;
  const month = dateParts.find((part) => part.type === 'month')?.value;
  return day && month ? `since ${day} ${month}` : 'unknown time';
};

export const formatUiNumber = (
  value: number | null | undefined,
  options: Intl.NumberFormatOptions = {}
): string => {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return INVALID_VALUE;
  }
  return new Intl.NumberFormat(UI_LOCALE, options).format(value);
};

export const formatUiPercent = (
  value: number | null | undefined,
  options: Intl.NumberFormatOptions = {}
): string => {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return INVALID_VALUE;
  }
  return new Intl.NumberFormat(UI_LOCALE, {
    style: 'percent',
    maximumFractionDigits: 1,
    ...options,
  }).format(value);
};

export const formatUiRelativeTime = (
  value: UiDateInput,
  now: UiDateInput = new Date()
): string => {
  const date = parseUiDate(value);
  const reference = parseUiDate(now);
  if (!date || !reference) {
    return 'Unknown time';
  }

  const seconds = Math.round((date.getTime() - reference.getTime()) / 1_000);
  const absoluteSeconds = Math.abs(seconds);
  const formatter = new Intl.RelativeTimeFormat(UI_LOCALE, {
    numeric: 'auto',
  });

  if (absoluteSeconds < 60) {
    return formatter.format(seconds, 'second');
  }
  if (absoluteSeconds < 3_600) {
    return formatter.format(Math.round(seconds / 60), 'minute');
  }
  if (absoluteSeconds < 86_400) {
    return formatter.format(Math.round(seconds / 3_600), 'hour');
  }
  if (absoluteSeconds < 2_592_000) {
    return formatter.format(Math.round(seconds / 86_400), 'day');
  }
  if (absoluteSeconds < 31_536_000) {
    return formatter.format(Math.round(seconds / 2_592_000), 'month');
  }
  return formatter.format(Math.round(seconds / 31_536_000), 'year');
};
